// Scene JSON validation: the single place a stored, shared or pasted scene
// string is parsed. A scene that doesn't match the shapes the editor restores
// from is rejected whole, so no caller ever half-restores from one.
import type { BuildScene } from "./build.js";
import type { StandaloneScene } from "./form.js";

export type Scene = BuildScene | StandaloneScene;

// Parse a scene JSON, or null if it isn't a scene this build can restore.
export function parseScene(json: string): Scene | null {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isObject(value)) return null;
  if (value.kind === "build") return buildScene(value);
  if (value.kind === "standalone") return standaloneScene(value);
  return null;
}

function buildScene(value: Record<string, unknown>): BuildScene | null {
  const size = finite(value.size);
  if (size == null || !Array.isArray(value.bits)) return null;

  const scene: BuildScene = { kind: "build", size, bits: [] };

  if (value.base !== undefined) {
    if (!isObject(value.base)) return null;
    const params = paramMap(value.base.params);
    if (params == null) return null;
    scene.base = { params };
  }

  if (value.topper !== undefined) {
    if (!isObject(value.topper)) return null;
    const archetypeId = id(value.topper.archetypeId);
    const params = paramMap(value.topper.params);
    if (archetypeId == null || params == null) return null;
    scene.topper = { archetypeId, params };
  }

  for (const entry of value.bits) {
    if (!isObject(entry)) return null;
    const archetypeId = id(entry.archetypeId);
    const params = paramMap(entry.params);
    const placement = paramMap(entry.placement);
    if (archetypeId == null || params == null || placement == null) return null;
    scene.bits.push({ archetypeId, params, placement });
  }

  return scene;
}

function standaloneScene(
  value: Record<string, unknown>,
): StandaloneScene | null {
  const archetypeId = id(value.archetypeId);
  const params = paramMap(value.params);
  if (archetypeId == null || params == null) return null;
  return { kind: "standalone", archetypeId, params };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

// Archetype ids index the registry, so anything but a non-negative integer
// names nothing.
function id(value: unknown): number | null {
  const n = finite(value);
  return n != null && Number.isInteger(n) && n >= 0 ? n : null;
}

// A param name -> value map; null unless every value is a finite number.
function paramMap(value: unknown): Record<string, number> | null {
  if (!isObject(value)) return null;
  const params: Record<string, number> = {};
  for (const [name, entry] of Object.entries(value)) {
    const n = finite(entry);
    if (n == null) return null;
    params[name] = n;
  }
  return params;
}
